import { InsightPollColdStartMillis } from '@marmon/shared/types/config';
import {
	Insight,
	InsightPollMaxAttempts,
	InsightPollResponse,
	timestampToDate,
} from '@marmon/shared/types/krista';
import {
	EMPTY,
	expand,
	map,
	Observable,
	of,
	switchMap,
	throwError,
	timer,
} from 'rxjs';

/** Milliseconds until the server will accept another poll. */
export function insightPollWaitMillis(
	nextPollAt: unknown,
	nowMillis = Date.now(),
): number {
	if (nextPollAt == null || typeof nextPollAt !== 'object') {
		return InsightPollColdStartMillis;
	}

	try {
		const at = timestampToDate(
			nextPollAt as Parameters<typeof timestampToDate>[0],
		).getTime();
		if (!Number.isFinite(at)) return InsightPollColdStartMillis;
		const remaining = at - nowMillis;
		return remaining > 0 ? remaining : InsightPollColdStartMillis;
	} catch {
		return InsightPollColdStartMillis;
	}
}

/**
 * Polls a pending insight until Krista finishes or the attempt cap is hit.
 * The first value is the insight already in hand; later values come from `poll`.
 */
export function followInsight(
	poll: (insightId: string) => Observable<InsightPollResponse>,
	start: Insight,
): Observable<Insight> {
	let polls = 0;

	return of(start).pipe(
		expand((insight) => {
			if (insight.status !== 'pending') return EMPTY;
			if (polls >= InsightPollMaxAttempts) {
				return throwError(() => new Error('Insight polling timed out'));
			}

			polls += 1;
			return timer(insightPollWaitMillis(insight.nextPollAt)).pipe(
				switchMap(() => poll(insight.id)),
				map((response) => response.insight),
			);
		}),
	);
}
